import { yupResolver } from "@hookform/resolvers/yup";
import { Modal, Box, Typography } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  ReusableButton,
  ReusableImageUpload,
  ReusableSelect,
  ReusableTextField,
} from "../../../reusable";
import { SectionHeader } from "../../widgets";
import {
  addOrEditProtocolSchema,
  addProtocolDefaultValues,
} from "../../../../schemas/protocols";

type Props = {
  open: boolean;
  handleClose: any;
  editProtocolObject: any;
  setEditProtocolObject: any;
  editProtocolFunction: any;
  addProtocolFunction: any;
};

const AddOrEditProtocolModal: React.FC<Props> = ({
  open,
  handleClose,
  editProtocolObject,
  setEditProtocolObject,
  editProtocolFunction,
  addProtocolFunction,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const form = useForm({
    defaultValues: editProtocolObject ?? addProtocolDefaultValues,
    resolver: yupResolver(addOrEditProtocolSchema),
  });

  const { handleSubmit, reset, control } = form;

  const onSubmit = async (value: any) => {
    if (isLoading) return;
    setIsLoading(true);

    if (editProtocolObject !== undefined) {
      editProtocolFunction(value);
      setIsLoading(false);
      setEditProtocolObject(undefined);
      handleClose();
      reset();
    } else {
      addProtocolFunction(value);
      setIsLoading(false);
      handleClose();
      reset();
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        setEditProtocolObject(undefined);
        reset();
        handleClose();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "24px",
      }}
    >
      <Box
        sx={{
          width: "70%",
          height: "95%",
          overflowY: "auto",
          backgroundColor: "background.paper",
          boxShadow: 5,
          outline: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "30px",
          padding: "8px 40px 8px 40px",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "32px",
                lineHeight: "40px",
                background: "linear-gradient(#F4AE24, #75512E)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              {`${
                editProtocolObject !== undefined
                  ? "Edit protocol"
                  : "Add protocol to program"
              }`}
            </Typography>
          </Box>
          <SectionHeader
            title={"Protocol Details"}
            subtitle={"Enter basic protocol details"}
          />
          <Box
            sx={{
              paddingLeft: "36px",
              marginTop: "20px",
              display: "flex",
              flexDirection: "row",
              width: "100%",
              gap: 6,
              padding: "24px 24px 16px 24px",
            }}
          >
            <Box sx={{ width: "50%", textAlign: "center" }}>
              <ReusableImageUpload
                form={form}
                name="image"
                label="Protocol Image"
              />
            </Box>
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "50%" }}
            >
              <ReusableTextField
                control={control}
                name="title"
                inputType="text"
                label="Protocol Name"
                placeholder="Name"
              />
              <ReusableSelect
                control={control}
                name={"category"}
                options={[
                  "Preparation",
                  "Application",
                  "Maintenance",
                  "Monitoring",
                  "Evaluation",
                  "Measurement",
                ]}
                label="Protocol Category"
                placeholder="Select protocol category"
              />
              <ReusableTextField
                control={control}
                name="dateRange"
                inputType="text"
                label="Date Range"
                placeholder="Enter a date range"
                startAdornment={
                  <Typography
                    sx={{
                      borderRight: "2px solid lightgrey",
                      paddingRight: "6px",
                    }}
                  >
                    Days
                  </Typography>
                }
              />
              <ReusableTextField
                control={control}
                name="steps"
                inputType="text"
                label="Steps"
                placeholder="Write steps description..."
                multiline={true}
                rows={5}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <ReusableButton
              buttonText={"Cancel"}
              color="#1C5553"
              backgroundColor="#D7E2E2"
              width="20%"
              onClickFunction={() => {
                setEditProtocolObject(undefined);
                handleClose();
                reset();
              }}
            />

            <ReusableButton
              buttonText={`${
                editProtocolObject !== undefined
                  ? "Edit protocol"
                  : "Add protocol"
              }`}
              width="20%"
              onClickFunction={handleSubmit(onSubmit)}
              isLoading={isLoading}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddOrEditProtocolModal;
